import React, { ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getConfigByPath } from '../../hooks/use-navbar-items';
import { MeetingOrganizationDetailsListType, PrivateMeetingDetailsResponseType } from '../../lib/api';
import { BACKGROUND_IMG_URL } from '../../lib/constants';
import { EventSettingsContextType, useEventSettings } from '../../lib/context-providers/event-settings-context/event-settings-context';
import MeetingTimingBlock from './MeetingTimingBlock';
import OrganizationsList from '../../app/shared/OrganizationsList';
import { AGENDA_PATH, SESSION_PATH } from '../../config/routes/paths';
import { Button } from '../../components/_base/Button';
import Icon from '../../components/_base/Icon';
import './MeetingDetails.scss';
import Buttons from '../../components/button';
import { isMeetLinksProfile } from '../../app-config';
import useMeetingCalendar from '../../hooks/use-meeting-calendar';
import OrganizationsListWithDetails from './OrganizationListWithDetails/OrganizationListWithDetails';

export interface IProps {
  meetingDetails: PrivateMeetingDetailsResponseType;
  showDetailsAllowed?: boolean;
  timeZone: string;
  children: ReactNode;
  showTopNavBtns?: boolean;
  agendaLabel?: string;
  eventName?: string;
  eventId?: string;
  showAddToCalendarBtn?: boolean;
  isMeetlink?: boolean;
  eventCustomTitle?: string
}

export const PREVENTED_DETAILS_TEXT = 'Connection Details will be provided before this meeting begins.';

const MeetingDetails: React.FunctionComponent<IProps> = (props: IProps) => {
  const [scrollToAttendeeDetails, setScrollToAttendeeDetails] = useState<{attendeeId: number} | null>(null);
  const data = useEventSettings() as EventSettingsContextType;
  const history = useHistory();
  const { fetchOutlookCalendarForMeeting, fetchGoogleCalendarForMeeting } = useMeetingCalendar();
  const path = window.location.pathname.startsWith(AGENDA_PATH) ? AGENDA_PATH : SESSION_PATH;
  const { label } = (path && data && getConfigByPath(path, data)) || { label: props.agendaLabel || 'Agenda' };

  const backToList = () => history.push(path || AGENDA_PATH);

  const { meetingDetails: details, showDetailsAllowed } = props;
  const connectionDetails = details.connectionDefined ? details.meetingInvitation : details.connectionDetails;
  const attendeesWithDetails: MeetingOrganizationDetailsListType = [];
  /* eslint-disable @typescript-eslint/no-unused-expressions */
  [
    ...(props.meetingDetails.hostOrganizations || []),
    ...(props.meetingDetails.participantOrganizations || []),
    ...(props.meetingDetails.presenterOrganizations || [])
  ].forEach(organization => {
    organization.attendees?.map(attendee => {
      if (attendee.biography || attendee.profileImageUrl) {
        const attendeeOrganization = attendeesWithDetails.find(el => el.organizationId === organization.organizationId);
        attendeeOrganization ? attendeeOrganization.attendees?.push(attendee) : attendeesWithDetails.push({ ...organization, attendees: [attendee]});
      }
      return attendee;
    });
    return organization;
  });
  /* eslint-enable @typescript-eslint/no-unused-expressions */

  const handleAttendeeClick = (attendeeId: number) => {
    setScrollToAttendeeDetails({ attendeeId });
  };

  return (
    <div className='md:w-3/4 w-100'>
      <div className='shadow-gray bg-white mx-auto md:px-8 px-0 p-8 mt-12 min-h-24rem'>
        {props.showTopNavBtns && <>
          <div className='px-8 pb-8'>
            <Button
              name='backToList'
              type='button'
              className='py-1 px-2 rounded-md text-primary bg-transparent on-hover-shadow'
              onClick={backToList}
              label={
                <>
                  <span className='pr-1'>
                    <Icon.RightArrow
                      className='mb-1 fill-primary transform rotate-180'
                      width='15px'
                      height='15px'
                    />
                  </span>
                  <span className='font-size-13px'>{label}</span>
                </>}
            >
            </Button>
          </div>
          <hr />
        </>}

        <div className='block sm:flex flex-none sm:flex-row items-start items-stretch max-h-full min-h-full'>
          <div className='flex-equal sm:flex-1 px-6 md:px-0 py-6 sm:pb-0 overflow-visible'>
            <div className='pt-2 xs:pt-8 pb-5 sm:pl-8 sm:pr-12'>
              <MeetingTimingBlock meetingDetails={props.meetingDetails} timeZone={props.timeZone} />
              {props.meetingDetails.type && <p className='text-sm'>
                <span className='text-primary text-xs font-bold'>Meeting type:&nbsp;</span>
                <span className='text-xs'>{props.meetingDetails.type}</span>
              </p>}
            </div>
            {(props.meetingDetails.topic || props.meetingDetails.notes) && <div className='pt-3 pb-5 px-0 sm:pl-8 sm:pr-12'>
              <h2 className='text-primary font-semibold font-size-18px pb-1'>{props.meetingDetails.topic}</h2>
              <p className='text-sm whitespace-pre-wrap break-words' dangerouslySetInnerHTML={{ __html: props.meetingDetails.notes }} />
            </div>}
            {props.meetingDetails.heroImageUrl &&
              <div className='mb-5 sm:ml-8 sm:mr-12'>
                <img src={props.meetingDetails.heroImageUrl} alt='hero'/>
              </div>}
            {props.showAddToCalendarBtn &&
              <div className='add-to-calendar-btn--wrapper sm:pl-8'>
                <Buttons.OptionsButton
                  label={'+ Add to Calendar'}
                  name={'addToCalendar'}
                  disabled={false}
                  eventId={props.eventId || ''}
                  eventName={props.eventName || ''}
                  className='select-box--button-small'
                  downloadIcsFile={() => fetchOutlookCalendarForMeeting(props.eventId || '', details.meetingId, `${props.eventCustomTitle || props.eventName}.ics`,)}
                  openGoogleCalendar={() => fetchGoogleCalendarForMeeting(props.eventId || '', details.meetingId)}
                />
              </div>
            }
            {props.meetingDetails.presenterOrganizations &&
              <OrganizationsList
                blockName=''
                organizations={props.meetingDetails.presenterOrganizations}
                attendeeClick={(id: number) => handleAttendeeClick(id)}
              />}
            {props.meetingDetails.participantOrganizations &&
              <OrganizationsList
                blockName={props.meetingDetails.presenterOrganizations ? 'Attendees:' : ''}
                organizations={props.meetingDetails.participantOrganizations}
                attendeeClick={(id: number) => handleAttendeeClick(id)}
              />}
            {props.meetingDetails.hostOrganizations &&
              <OrganizationsList
                blockName='Hosted by:'
                organizations={props.meetingDetails.hostOrganizations}
                attendeeClick={(id: number) => handleAttendeeClick(id)}
              />}
            {attendeesWithDetails.length > 0 &&
              <OrganizationsListWithDetails
                organizations={attendeesWithDetails}
                scrollToAttendee={scrollToAttendeeDetails}
              />}
          </div>
          <div className='flex-equal sm:flex-1 overflow-hidden bg-cool-gray'>
            {props.isMeetlink ?
              (props.children || connectionDetails ?
                <div className='overflow-x-auto pb-8'>
                  {props.children &&
                    <div className='text-left p-8'>
                      {props.children}
                    </div>}
                  {connectionDetails && <div className={`font-size-14px ${props.children ? 'px-8' : 'p-8'}`}>
                    <div
                      style={{ borderTop: props.meetingDetails.connectionLink ? '1px solid #b2bfc5' : 'none' }}
                      className='connection-details-title font-semibold'
                    >
                      Connection Details
                    </div>
                    <div className='pt-4 whitespace-pre-wrap leading-loose'>
                      {showDetailsAllowed ? connectionDetails : PREVENTED_DETAILS_TEXT}
                    </div>
                  </div>
                  }
                </div> :
                <div className='text-left p-8 text-sm'>
                  Connection details will be provided ahead of the meeting. Stand by.
                </div>)
              :
              // eslint-disable-next-line react/jsx-indent
              <>
                <div className='text-left p-8'>
                  {props.children}
                </div>
                {connectionDetails && <div className='px-8 font-size-14px'>
                  <div className='connection-details-title font-semibold'>Connection Details</div>
                  <div className='pt-4 whitespace-pre-wrap leading-loose'>
                    {showDetailsAllowed ? connectionDetails : PREVENTED_DETAILS_TEXT}
                  </div>
                </div>
                }
              </>
            }
          </div>
        </div>
        {!props.meetingDetails && <p className='text-center pt-4 pb-8'>No meeting details yet</p>}
      </div>
      {isMeetLinksProfile() &&
        <img
          className='background-img'
          src={details.backgroundFile || BACKGROUND_IMG_URL}
          alt={details.backgroundTitle || 'Background image'}
        />}
    </div>
  );
};

MeetingDetails.defaultProps = {
  showDetailsAllowed: true
};

export default MeetingDetails;
